import { createContext, type ReactNode, useContext, useState } from 'react';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type {
  Condition,
  PositionedItem,
} from '../PageConfigProvider/types/item';

type ConditionalChart<TData extends object = object> = {
  chartId: string;
  isVisible: boolean;
  chartData: TData | null;
};

type TriggerCondition = Condition & {
  chartId: string;
  toggleChartVisible: (data: object) => void;
};

interface ConditionalChartContextValue {
  condiationalCharts: ConditionalChart[];
  triggerConditions: TriggerCondition[];
}

const ConditionalChartContext = createContext<
  ConditionalChartContextValue | undefined
>(undefined);

export const ConditionalChartProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const {
    config: { items },
  } = useTabContentConfig();
  const chartConditions = items
    .filter(
      (
        item
      ): item is PositionedItem & Required<Pick<PositionedItem, 'condition'>> =>
        !!item.condition
    )
    .map((item) => ({ chartId: item.id, ...item.condition }));

  const [condiationalCharts, setCondiationalCharts] = useState<
    ConditionalChart[]
  >(
    chartConditions.map((chartCondition) => ({
      chartId: chartCondition.chartId,
      isVisible: false,
      chartData: null,
    }))
  );

  const triggerConditions: TriggerCondition[] = chartConditions.map(
    (chartCondition) => {
      const chartToMakeVisible = chartCondition.chartId;

      return {
        ...chartCondition,
        toggleChartVisible: (data) => {
          setCondiationalCharts((prev) =>
            prev.map((chart) =>
              chart.chartId === chartToMakeVisible
                ? { ...chart, isVisible: true, chartData: data }
                : chart
            )
          );
        },
      };
    }
  );

  return (
    <ConditionalChartContext.Provider
      value={{ condiationalCharts, triggerConditions }}
    >
      {children}
    </ConditionalChartContext.Provider>
  );
};

export const useConditionalChart = () => {
  const context = useContext(ConditionalChartContext);
  if (!context) {
    throw new Error(
      'useConditionalChart must be used within a ConditionalChartProvider'
    );
  }
  return context;
};
